import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Wallet, Plus, ArrowLeft, RefreshCw, DollarSign, User, Truck, X, CheckCircle2, Clock } from 'lucide-react'
import { api, storeTransferApi, currencyDenominationsApi } from '../../../lib/api'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

interface DeliveryWallet {
  delivery_id: string
  delivery_name: string
  delivery_email: string
  current_balance: number
  store_id: string
}

interface Transfer {
  id: string
  amount: number
  status: 'pending' | 'confirmed' | 'cancelled'
  description: string
  created_at: string
  confirmed_at?: string
  delivery_name: string
  delivery_email: string
  created_by_name: string
  breakdown?: Array<{
    denomination: number
    name: string
    type: string
    quantity: number
    total_amount: number
  }>
}

interface Denomination {
  id: string
  denomination: number
  name: string
  type: 'bill' | 'coin'
  is_active: boolean
}

const ManageDeliveryWallets = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const storeId = params.storeId || user?.storeId

  const [deliveries, setDeliveries] = useState<DeliveryWallet[]>([])
  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [denominations, setDenominations] = useState<Denomination[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryWallet | null>(null)
  const [transferAmount, setTransferAmount] = useState('')
  const [transferDescription, setTransferDescription] = useState('')
  const [transferBreakdown, setTransferBreakdown] = useState<Record<string, number>>({})
  const [creatingTransfer, setCreatingTransfer] = useState(false)
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all')

  useEffect(() => {
    if (storeId) {
      loadData()
    }
  }, [storeId, filterStatus])

  const loadData = async () => {
    if (!storeId) return

    try {
      setRefreshing(true)
      await Promise.all([
        loadDeliveries(),
        loadTransfers(),
        loadDenominations()
      ])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const loadDeliveries = async () => {
    if (!storeId) return

    const cacheKey = `delivery_wallets_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedDeliveries = getCacheStale<DeliveryWallet[]>(cacheKey)
    if (cachedDeliveries && cachedDeliveries.length > 0) {
      setDeliveries(cachedDeliveries)
    }

    try {
      // Obtener todas las carteras de delivery del store
      const walletsResponse = await storeTransferApi.getStoreWallets(storeId)
      if (walletsResponse.success) {
        const wallets = walletsResponse.data || []
        const deliveriesData = wallets.map((w: any) => ({
          delivery_id: w.delivery_id,
          delivery_name: w.delivery_name,
          delivery_email: w.delivery_email,
          current_balance: Number(w.current_balance || 0),
          store_id: w.store_id
        }))
        setDeliveries(deliveriesData)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, deliveriesData, 5 * 60 * 1000)
      } else {
        // Si no hay carteras, obtener empleados delivery y crear carteras vacías
        const response = await api.get(`/auth/store/${storeId}/employees`)
        if (response.data.success) {
          const employees = response.data.data || []
          const deliveryEmployees = employees.filter((e: any) => e.type === 'delivery' && e.is_active)
          
          const deliveriesData = deliveryEmployees.map((employee: any) => ({
            delivery_id: employee.id,
            delivery_name: employee.name,
            delivery_email: employee.email,
            current_balance: 0,
            store_id: storeId
          }))
          
          setDeliveries(deliveriesData)
          
          // Guardar en cache (5 minutos de TTL)
          setCache(cacheKey, deliveriesData, 5 * 60 * 1000)
        }
      }
    } catch (error: any) {
      console.error('Error loading deliveries:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedDeliveries) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar carteras de delivery')
    }
  }

  const loadTransfers = async () => {
    if (!storeId) return

    const cacheKey = `delivery_transfers_${storeId}_${filterStatus}`
    
    // Intentar cargar desde cache primero
    const cachedTransfers = getCacheStale<Transfer[]>(cacheKey)
    if (cachedTransfers && cachedTransfers.length > 0) {
      setTransfers(cachedTransfers)
    }

    try {
      const status = filterStatus === 'all' ? undefined : filterStatus
      const response = await storeTransferApi.getTransfers(storeId, status)
      if (response.success) {
        const transfersData = response.data || []
        setTransfers(transfersData)
        
        // Guardar en cache (2 minutos de TTL - las transferencias cambian frecuentemente)
        setCache(cacheKey, transfersData, 2 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading transfers:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedTransfers) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar transferencias')
    }
  }

  const loadDenominations = async () => {
    const cacheKey = 'currency_denominations'
    
    // Intentar cargar desde cache primero
    const cachedDenominations = getCacheStale<Denomination[]>(cacheKey)
    if (cachedDenominations && cachedDenominations.length > 0) {
      setDenominations(cachedDenominations)
    }

    try {
      const response = await currencyDenominationsApi.getDenominations()
      if (response.success) {
        const denominationsData = response.data || []
        setDenominations(denominationsData)
        
        // Guardar en cache (24 horas de TTL - las denominaciones cambian muy poco)
        setCache(cacheKey, denominationsData, 24 * 60 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading denominations:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedDenominations) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
    }
  }

  const handleCreateTransfer = () => {
    if (!selectedDelivery) return
    
    setTransferAmount('')
    setTransferDescription('')
    setTransferBreakdown({})
    setShowTransferModal(true)
  }

  const updateBreakdown = (denominationId: string, quantity: number) => {
    setTransferBreakdown(prev => ({
      ...prev,
      [denominationId]: quantity
    }))
    
    // Calcular total automáticamente
    const total = Object.entries({ ...transferBreakdown, [denominationId]: quantity }).reduce((sum, [id, qty]) => {
      const denom = denominations.find(d => d.id === id)
      return sum + (denom ? denom.denomination * (qty || 0) : 0)
    }, 0)
    
    setTransferAmount(total.toFixed(2))
  }

  const calculateBreakdownTotal = () => {
    return Object.entries(transferBreakdown).reduce((sum, [id, quantity]) => {
      const denom = denominations.find(d => d.id === id)
      return sum + (denom ? denom.denomination * (quantity || 0) : 0)
    }, 0)
  }

  const handleSubmitTransfer = async () => {
    if (!selectedDelivery || !storeId) return

    const amount = parseFloat(transferAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error('Ingrese un monto válido')
      return
    }

    const breakdownTotal = calculateBreakdownTotal()
    if (Math.abs(breakdownTotal - amount) > 0.01) {
      toast.error('El desglose no coincide con el monto total')
      return
    }

    const breakdown = Object.entries(transferBreakdown)
      .filter(([_, qty]) => qty > 0)
      .map(([denominationId, quantity]) => ({
        denominationId,
        quantity: quantity || 0
      }))

    if (breakdown.length === 0) {
      toast.error('Ingrese el desglose de billetes/monedas')
      return
    }

    try {
      setCreatingTransfer(true)
      const response = await storeTransferApi.createTransfer(storeId, {
        deliveryId: selectedDelivery.delivery_id,
        amount,
        breakdown,
        description: transferDescription || `Transferencia de dinero para devueltas - ${selectedDelivery.delivery_name}`
      })

      if (response.success) {
        toast.success('Transferencia creada exitosamente')
        setShowTransferModal(false)
        setSelectedDelivery(null)
        setTransferAmount('')
        setTransferDescription('')
        setTransferBreakdown({})
        await loadData()
      } else {
        toast.error(response.message || 'Error al crear transferencia')
      }
    } catch (error: any) {
      console.error('Error creating transfer:', error)
      toast.error(error.response?.data?.message || 'Error al crear transferencia')
    } finally {
      setCreatingTransfer(false)
    }
  }

  const handleCancelTransfer = async (transferId: string) => {
    if (!storeId) return

    if (!confirm('¿Está seguro de cancelar esta transferencia?')) return

    try {
      const response = await storeTransferApi.cancelTransfer(storeId, transferId)
      if (response.success) {
        toast.success('Transferencia cancelada exitosamente')
        await loadData()
      } else {
        toast.error(response.message || 'Error al cancelar transferencia')
      }
    } catch (error: any) {
      console.error('Error canceling transfer:', error)
      toast.error(error.response?.data?.message || 'Error al cancelar transferencia')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 pb-20">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-4 mb-4 sm:mb-6">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/70 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900 tracking-tight">Carteras de Delivery</h1>
              <p className="text-sm sm:text-base text-gray-500 mt-1">Gestionar carteras y transferencias de dinero</p>
            </div>
            <button
              onClick={loadData}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/80 backdrop-blur-xl text-gray-700 rounded-xl font-medium shadow-lg shadow-black/5 border border-gray-200/50 hover:bg-white hover:shadow-xl transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualizar</span>
            </button>
          </div>
        </div>

        {/* Delivery Wallets */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg sm:text-xl font-light text-gray-900">Carteras de Delivery</h2>
            <button
              onClick={handleCreateTransfer}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/20 hover:from-blue-600 hover:to-blue-700 transition-all active:scale-95"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva Transferencia</span>
              <span className="sm:hidden">Transferir</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {deliveries.map((delivery) => (
              <div
                key={delivery.delivery_id}
                className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6 hover:shadow-2xl transition-all"
              >
                <div className="flex items-center gap-3 sm:gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                    <Truck className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate text-sm sm:text-base">{delivery.delivery_name}</p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">{delivery.delivery_email}</p>
                  </div>
                </div>
                <div className="border-t border-gray-200/50 pt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Balance Actual</p>
                  <p className="text-2xl sm:text-3xl font-light text-emerald-600">RD${Number(delivery.current_balance || 0).toFixed(2)}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedDelivery(delivery)
                    handleCreateTransfer()
                  }}
                  className="mt-4 w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/20 hover:from-blue-600 hover:to-blue-700 transition-all text-sm"
                >
                  Transferir Dinero
                </button>
              </div>
            ))}

            {deliveries.length === 0 && (
              <div className="col-span-full bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-8 sm:p-12 text-center">
                <Wallet className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-sm sm:text-base">No hay delivery disponibles</p>
              </div>
            )}
          </div>
        </div>

        {/* Transfers History */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6 md:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-light text-gray-900">Historial de Transferencias</h2>
            <div className="flex gap-2 bg-white/50 backdrop-blur-sm rounded-xl p-1 border border-gray-200/50">
              {(['all', 'pending', 'confirmed', 'cancelled'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    filterStatus === status
                      ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100/50'
                  }`}
                >
                  {status === 'all' ? 'Todas' : status === 'pending' ? 'Pendientes' : status === 'confirmed' ? 'Confirmadas' : 'Canceladas'}
                </button>
              ))}
            </div>
          </div>

          {transfers.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-gray-400 text-sm sm:text-base">No hay transferencias en el período seleccionado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-100/50 hover:bg-white/70 transition-all"
                >
                  <div className="flex-1 min-w-0 mb-3 sm:mb-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md flex-shrink-0 ${
                        transfer.status === 'confirmed'
                          ? 'bg-gradient-to-br from-emerald-400 to-emerald-500 shadow-emerald-500/20'
                          : transfer.status === 'pending'
                          ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 shadow-yellow-500/20'
                          : 'bg-gradient-to-br from-red-400 to-red-500 shadow-red-500/20'
                      }`}>
                        {transfer.status === 'confirmed' ? (
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        ) : transfer.status === 'pending' ? (
                          <Clock className="w-5 h-5 text-white" />
                        ) : (
                          <X className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{transfer.delivery_name}</p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{transfer.delivery_email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(transfer.created_at).toLocaleDateString('es-DO', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    {transfer.description && (
                      <p className="text-xs sm:text-sm text-gray-600 ml-13 mt-2">{transfer.description}</p>
                    )}
                    {transfer.breakdown && transfer.breakdown.length > 0 && (
                      <div className="mt-2 ml-14 flex flex-wrap gap-2">
                        {transfer.breakdown.map((item, idx) => (
                          <span key={idx} className="text-xs bg-gray-100/50 px-2 py-1 rounded-lg">
                            {item.quantity}x {item.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="text-right">
                      <p className="text-lg sm:text-xl font-light text-gray-900">RD${transfer.amount.toFixed(2)}</p>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        transfer.status === 'confirmed'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : transfer.status === 'pending'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {transfer.status === 'confirmed' ? 'Confirmada' : transfer.status === 'pending' ? 'Pendiente' : 'Cancelada'}
                      </span>
                    </div>
                    {transfer.status === 'pending' && (
                      <button
                        onClick={() => handleCancelTransfer(transfer.id)}
                        className="px-3 py-1.5 bg-white/80 backdrop-blur-sm text-red-600 rounded-xl border border-red-200/50 hover:bg-red-50 transition-all text-xs sm:text-sm font-medium"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Transfer Modal */}
      {showTransferModal && selectedDelivery && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl shadow-black/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/60">
            <div className="p-4 sm:p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-light text-gray-900">
                  Transferir Dinero a {selectedDelivery.delivery_name}
                </h3>
                <button
                  onClick={() => {
                    setShowTransferModal(false)
                    setSelectedDelivery(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Monto Total</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Desglose de Billetes/Monedas</label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {denominations.map((denom) => (
                    <div key={denom.id} className="flex items-center justify-between p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-gray-200/50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          denom.type === 'bill' ? 'bg-gradient-to-br from-green-400 to-green-500' : 'bg-gradient-to-br from-yellow-400 to-yellow-500'
                        }`}>
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{denom.name}</p>
                          <p className="text-xs text-gray-500">RD${denom.denomination.toFixed(2)}</p>
                        </div>
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={transferBreakdown[denom.id] || 0}
                        onChange={(e) => updateBreakdown(denom.id, parseInt(e.target.value) || 0)}
                        className="w-20 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-gray-50/50 rounded-xl">
                  <p className="text-sm text-gray-600">
                    Total del desglose: <span className="font-semibold text-gray-900">RD${calculateBreakdownTotal().toFixed(2)}</span>
                  </p>
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción (opcional)</label>
                <textarea
                  value={transferDescription}
                  onChange={(e) => setTransferDescription(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Ej: Dinero para devueltas del día"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false)
                    setSelectedDelivery(null)
                  }}
                  className="flex-1 px-4 py-2.5 bg-white/80 backdrop-blur-sm text-gray-700 rounded-xl border border-gray-200/50 hover:bg-gray-50 transition-all font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitTransfer}
                  disabled={creatingTransfer || !transferAmount || parseFloat(transferAmount) <= 0}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/20 hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingTransfer ? 'Creando...' : 'Crear Transferencia'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageDeliveryWallets

